import { ChevronDown, ShapesIcon } from "lucide-react";
import React, { useMemo, useState } from "react";

import { restoreSelection, saveSelection } from "@/utils/selection";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";

import ToolbarButton from "./toolbar-button";

type ShapeOptions = {
  fillColor: string;
  borderColor: string;
  borderWidth: number;
  textColor: string;
  textAlign: React.CSSProperties["textAlign"];
};

type ShapeDefinition = {
  id: string;
  label: string;
  style: (options: ShapeOptions) => React.CSSProperties;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const DEFAULT_WIDTH = 240;
const DEFAULT_HEIGHT = 140;
const MIN_WIDTH = 10;
const MIN_HEIGHT = 10;

// const getJustifyContentValue = (
//   value?: React.CSSProperties["textAlign"]
// ): React.CSSProperties["justifyContent"] => {
//   switch (value) {
//     case "left":
//     case "start":
//       return "flex-start";
//     case "right":
//     case "end":
//       return "flex-end";
//     case "justify":
//       return "stretch";
//     case "center":
//     default:
//       return "center";
//   }
// };

const baseShapeStyle = (options: ShapeOptions): React.CSSProperties => ({
  backgroundColor: options.fillColor,
  border: `${options.borderWidth}px solid ${options.borderColor}`,
  color: options.textColor
});

const SHAPES: ShapeDefinition[] = [
  {
    id: "rectangle",
    label: "مستطیل",
    style: (options) => ({
      ...baseShapeStyle(options),
      borderRadius: "0rem"
    })
  },
  {
    id: "rounded",
    label: "مستطیل گرد",
    style: (options) => ({
      ...baseShapeStyle(options),
      borderRadius: "1.25rem"
    })
  },
  {
    id: "pill",
    label: "کپسولی",
    style: (options) => ({
      ...baseShapeStyle(options),
      borderRadius: "999px"
    })
  },
  {
    id: "circle",
    label: "دایره",
    style: (options) => ({
      ...baseShapeStyle(options),
      borderRadius: "999px",
      aspectRatio: "1 / 1"
    })
  },
  {
    id: "ellipse",
    label: "بیضی",
    style: (options) => ({
      ...baseShapeStyle(options),
      borderRadius: "50%"
    })
  },
  {
    id: "diamond",
    label: "لوزی",
    style: (options) => ({
      ...baseShapeStyle(options),
      clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)"
    })
  },
  {
    id: "triangle",
    label: "مثلث",
    style: (options) => ({
      ...baseShapeStyle(options),
      clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
      alignItems: "flex-end"
    })
  },
  {
    id: "arrow",
    label: "فلش راست",
    style: (options) => ({
      ...baseShapeStyle(options),
      clipPath:
        "polygon(0% 20%, 70% 20%, 70% 0%, 100% 50%, 70% 100%, 70% 80%, 0% 80%)"
    })
  }
];

const applyStylesToElement = (
  element: HTMLElement,
  styles: React.CSSProperties
) => {
  Object.entries(styles).forEach(([property, value]) => {
    if (value === undefined || value === null) return;
    const cssValue = typeof value === "number" ? value.toString() : value;
    (element.style as any)[property] = cssValue;
  });
};

const InsertShape: React.FC<{
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ editorRef }) => {
  const [fillColor, setFillColor] = useState("#E0F2FE");
  const [borderColor, setBorderColor] = useState("#0f172a");
  const [textColor, setTextColor] = useState("#0f172a");
  const [borderWidth, setBorderWidth] = useState(2);
  const [placeholder, setPlaceholder] = useState("متن خود را اینجا بنویسید");
  const [hasInitialText, setHasInitialText] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [textAlign, setTextAlign] =
    useState<React.CSSProperties["textAlign"]>("center");

  const options = useMemo(
    () => ({ fillColor, borderColor, borderWidth, textColor, textAlign }),
    [fillColor, borderColor, borderWidth, textColor, textAlign]
  );

  const getPreviewStyle = (shape: ShapeDefinition) => {
    const style = shape.style(options);
    return {
      ...style,
      width: "100%",
      height: "100%"
    };
  };

  const insertShapeIntoEditor = (shape: ShapeDefinition) => {
    if (!editorRef.current) return;

    const placeholderText = hasInitialText
      ? placeholder.trim() || "روی شکل کلیک و تایپ کنید"
      : "";

    const wrapper = document.createElement("div");
    wrapper.contentEditable = "false";
    wrapper.dataset.shapeContainer = shape.id;
    wrapper.style.display = isFullWidth ? "block" : "inline-block";
    wrapper.style.width = isFullWidth ? "100%" : "auto";
    wrapper.style.maxWidth = "100%";
    wrapper.style.padding = "0";
    wrapper.style.margin = "0";
    wrapper.style.border = "none";
    wrapper.style.backgroundColor = "transparent";
    wrapper.style.overflow = "visible";

    const shapeElement = document.createElement("div");
    shapeElement.contentEditable = "true";
    shapeElement.dataset.shape = shape.id;
    if (placeholderText) {
      shapeElement.dataset.placeholder = placeholderText;
    } else {
      delete shapeElement.dataset.placeholder;
    }
    shapeElement.spellcheck = true;
    shapeElement.style.outline = "none";
    shapeElement.style.display = "inline-block";
    shapeElement.style.minWidth = `${MIN_WIDTH}px`;
    shapeElement.style.minHeight = `${MIN_HEIGHT}px`;
    shapeElement.style.width = isFullWidth ? "100%" : `${DEFAULT_WIDTH}px`;
    shapeElement.style.maxWidth = "100%";
    shapeElement.style.height = `${DEFAULT_HEIGHT}px`;
    // allow resizing by user; overflow must not be 'visible'
    shapeElement.style.overflow = "auto";
    shapeElement.style.resize = isFullWidth ? "vertical" : "both";
    shapeElement.style.cursor = "text";
    shapeElement.dir = "auto";

    const appliedStyle = shape.style(options);
    applyStylesToElement(shapeElement, appliedStyle);
    // keep only text alignment from controls, avoid forcing layout styles
    shapeElement.style.textAlign = textAlign ?? "center";

    if (placeholderText) {
      shapeElement.textContent = placeholderText;
      shapeElement.addEventListener("focus", () => {
        if (shapeElement.textContent === placeholderText) {
          shapeElement.textContent = "";
        }
      });
      shapeElement.addEventListener("blur", () => {
        if (!shapeElement.textContent?.trim()) {
          shapeElement.textContent = placeholderText;
        }
      });
    } else {
      shapeElement.textContent = "";
    }

    wrapper.appendChild(shapeElement);

    restoreSelection();
    const selection = window.getSelection();
    const spacer = document.createElement("br");
    spacer.contentEditable = "false";

    if (selection?.rangeCount) {
      const range = selection.getRangeAt(0);
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(wrapper);
        range.setStartAfter(wrapper);
        range.setEndAfter(wrapper);
        range.insertNode(spacer);
        range.setStartAfter(spacer);
        range.setEndAfter(spacer);
        editorRef.current.focus();
        return;
      }
    }

    editorRef.current.appendChild(wrapper);
    editorRef.current.appendChild(spacer);
    editorRef.current.focus();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <ToolbarButton
          className="flex w-fit items-center gap-x-2"
          onClick={() => {
            saveSelection();
          }}
        >
          <ShapesIcon />
          Shapes
          <ChevronDown />
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1 text-sm">
            <label className="text-xs text-slate-500">رنگ داخل</label>
            <input
              type="color"
              value={fillColor}
              className="border-input bg-background h-10 w-full rounded-md border"
              onChange={(event) => setFillColor(event.target.value)}
            />
          </div>
          <div className="space-y-1 text-sm">
            <label className="text-xs text-slate-500">رنگ خط</label>
            <input
              type="color"
              value={borderColor}
              className="border-input bg-background h-10 w-full rounded-md border"
              onChange={(event) => setBorderColor(event.target.value)}
            />
          </div>
          <div className="space-y-1 text-sm">
            <label className="text-xs text-slate-500">رنگ متن</label>
            <input
              type="color"
              value={textColor}
              className="border-input bg-background h-10 w-full rounded-md border"
              onChange={(event) => setTextColor(event.target.value)}
            />
          </div>
          <div className="space-y-1 text-sm">
            <label className="text-xs text-slate-500">چیدمان متن</label>
            <select
              value={textAlign}
              className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm"
              onChange={(event) =>
                setTextAlign(
                  event.target.value as React.CSSProperties["textAlign"]
                )
              }
            >
              <option value="right">راست چین</option>
              <option value="center">وسط چین</option>
              <option value="left">چپ چین</option>
              <option value="justify">تراز شده</option>
            </select>
          </div>
          <div className="space-y-1 text-sm">
            <label className="text-xs text-slate-500">ضخامت خط</label>
            <Input
              type="number"
              min={1}
              max={12}
              value={borderWidth}
              onChange={(event) =>
                setBorderWidth(clamp(Number(event.target.value) || 1, 1, 12))
              }
            />
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-x-2">
            <label className="text-xs text-slate-500">متن اولیه</label>
            <Switch
              checked={hasInitialText}
              onCheckedChange={setHasInitialText}
            />
          </div>
          <label className="flex items-center gap-x-2 text-xs text-slate-500">
            <Checkbox
              checked={isFullWidth}
              onCheckedChange={(checked) => setIsFullWidth(checked === true)}
            />
            عرض کامل صفحه
          </label>
          {hasInitialText && (
            <Input
              type="text"
              value={placeholder}
              onChange={(event) => setPlaceholder(event.target.value)}
              placeholder="مثلا: تیتر جدید"
            />
          )}
        </div>
        <ScrollArea className="h-72">
          <div className="grid grid-cols-2 gap-3 pb-2">
            {SHAPES.map((shape) => (
              <Button
                key={shape.id}
                variant="outline"
                className="flex h-36 flex-col items-center gap-y-2 border-dashed"
                onClick={() => insertShapeIntoEditor(shape)}
              >
                <div className="w-full flex-1 rounded-md bg-slate-50 p-2">
                  <div
                    className="flex h-full w-full items-center justify-center text-xs font-medium"
                    style={getPreviewStyle(shape)}
                  >
                    Aa
                  </div>
                </div>
                <span className="text-xs text-slate-600">{shape.label}</span>
              </Button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
export default InsertShape;
